"use client";

import Image from "next/legacy/image";
import { useState } from "react";
import { imageBuilder } from "../lib/sanity";
import { blurDataURL } from "../lib/constants";
import { useEffect, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";

export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

interface Author {
  name?: string;
  image?: any; // Sanity image type
}

interface Testimonial {
  quote: string;
  author?: Author;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
  className?: string;
}

export function Testimonials({
  testimonials,
  className = "",
}: TestimonialsProps) {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const hasMultipleTestimonials = testimonials.length > 1;

  const handleNext = () => {
    setInteracted(true);
    setCurrentTestimonial((current) =>
      current === testimonials.length - 1 ? 0 : current + 1
    );
  };

  const handlePrev = () => {
    setInteracted(true);
    setCurrentTestimonial((current) =>
      current === 0 ? testimonials.length - 1 : current - 1
    );
  };

  useInterval(() => {
    if (interacted || !hasMultipleTestimonials) return;
    handleNext();
  }, 12000);

  if (!testimonials.length) return null;

  return (
    <div className={`border-t border-gray-200 pt-4 ${className}`}>
      {hasMultipleTestimonials && (
        <div className="flex items-center gap-2 justify-center mb-4">
          <button
            onClick={handlePrev}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
          </button>

          <nav className="flex items-center" aria-label="Testimonials">
            <ol role="list" className="flex items-center space-x-3">
              {testimonials.map((_, index) => (
                <li key={index}>
                  {index === currentTestimonial ? (
                    <button
                      className="relative flex items-center justify-center"
                      aria-current="step"
                    >
                      <span
                        className="absolute w-4 h-4 p-px flex"
                        aria-hidden="true"
                      >
                        <span className="w-full h-full rounded-full bg-accent-200" />
                      </span>
                      <span
                        className="relative block w-2 h-2 bg-accent-600 rounded-full"
                        aria-hidden="true"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setInteracted(true);
                        setCurrentTestimonial(index);
                      }}
                      className="block w-2 h-2 bg-gray-200 rounded-full hover:bg-gray-400 transition"
                    />
                  )}
                </li>
              ))}
            </ol>
          </nav>

          <button
            onClick={handleNext}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      )}

      <blockquote className="min-h-[372px]">
        <div>
          <p className="text-base text-gray-700 whitespace-pre-wrap font-semibold italic">
            &ldquo;{testimonials[currentTestimonial].quote}&rdquo;
          </p>
        </div>
        <footer className="mt-3">
          <div className="flex items-center space-x-3">
            {testimonials[currentTestimonial].author?.image ? (
              <div className="flex-shrink-0">
                <div className="h-6 w-6 rounded-full relative">
                  <Image
                    draggable={false}
                    alt=""
                    layout="fill"
                    className="rounded-full"
                    placeholder="blur"
                    blurDataURL={blurDataURL}
                    src={imageBuilder(
                      testimonials[currentTestimonial].author?.image
                    )
                      .width(24)
                      .height(24)
                      .dpr(2)
                      .crop("entropy")
                      .url()}
                  />
                </div>
              </div>
            ) : null}

            <div className="text-base font-medium text-gray-500">
              {testimonials[currentTestimonial].author?.name}
            </div>
          </div>
        </footer>
      </blockquote>
    </div>
  );
}
